import { gql, useQuery } from '@apollo/client';
import type { Group } from '@domain/person';

const query = gql`
  query groups {
    groups {
      name
      description
    }
  }
`;

interface GroupsQuery {
  groups: Group[];
}

interface UseGroupsReturns {
  loading: boolean;
  groups?: Group[];
}

export default function useGroups(): UseGroupsReturns {
  const { error, loading, data } = useQuery<GroupsQuery>(query);
  if (error != null) throw error;
  if (loading) {
    return { loading: true };
  } else {
    if (data == null)
      throw new Error('useGroups: loading finished but data is undefined');
    return { loading: false, groups: data.groups };
  }
}

export const groupsQuery = gql`
  query ($idnumber: String!) {
    person(idnumber: $idnumber) {
      darn
      groups {
        name
        description
        virtual
      }
    }
  }
`;

interface PersonGroupsQuery {
  person: {
    darn: number;
    groups: Group[];
  };
}

interface UsePersonGroupsReturns {
  groups?: Group[];
  loading: boolean;
  refetch: () => Promise<void>;
}

interface PersonQueryVars {
  idnumber: string;
}

export function usePersonGroups(idnumber: string): UsePersonGroupsReturns {
  const { error, loading, data, refetch } = useQuery<
    PersonGroupsQuery,
    PersonQueryVars
  >(groupsQuery, { variables: { idnumber }, fetchPolicy: 'cache-and-network' });
  if (error != null) throw error;
  return {
    groups: data?.person.groups,
    loading,
    refetch: async () => {
      await refetch();
    },
  };
}
